import { DeliveryWindowsColumn } from '@app/components/orders/OrdersList/OrdersListTable/DeliveryWindowsColumn';
import { Notification } from '@app/components/various/Notification';
import { StatusCell, Table } from '@app/components/various/Table';
import { getAllDeliveryWindows } from '@app/ducks/deliveryWindows';
import { getCheckoutSplitData, setIsRequestingSplitOrder } from '@app/ducks/order';
import { getOrderPathByStatus, getPrepaymentFromOrder } from '@app/logic/Orders';
import { formatPriceWithCurrencyAffix } from '@app/logic/price';
import { useUrlOrderIdMatch } from '@app/utils/hooks';
import { useTable } from '@app/utils/hooks/table/useTable';
import { useOrderStatusLabels } from '@app/utils/hooks/useOrderStatusLabels';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { getOrderStatusColor } from '@app/utils/status';
import { renderStandardCell } from '@app/utils/table';
import { createColumnHelper, getCoreRowModel, getSortedRowModel } from '@tanstack/react-table';
import { Order } from '@typings';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { CheckoutSplitCard } from './CheckoutSplitCard';
import { CheckoutSplitCardSkeleton } from './CheckoutSplitCardSkeleton';

interface TableRow {
  deliveryWindowsIds: string[];
  key: string;
  orderId: string;
  orderNumber: string;
  status: Order.Status;
  displayDate: string | null;
  total: string;
  prepaymentAmount: string;
  units: number;
}

const columnHelper = createColumnHelper<TableRow>();

export const CheckoutSplitSuccess = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['common', 'checkout', 'checkoutMessages', 'totals', 'orders']);
  const allDeliveryWindows = useSelector(getAllDeliveryWindows);
  const { currency, orders } = useSelector(getCheckoutSplitData);
  const { isMatchingSplitOrderId } = useUrlOrderIdMatch();
  const statusLabel = useOrderStatusLabels();
  const priceFormatter = React.useMemo(() => formatPriceWithCurrencyAffix(currency), [currency]);

  React.useEffect(() => {
    if (isMatchingSplitOrderId) {
      dispatch(setIsRequestingSplitOrder(false));
    }
  }, [dispatch, isMatchingSplitOrderId]);

  const renderOrderNumberCell = React.useCallback((record: TableRow) => {
    if (!isDefined(record.orderId)) {
      return '';
    }

    const path = getOrderPathByStatus(record.orderId, record.status as Order.Status);

    return (
      <Link to={path} className="table-row-interaction body-1">
        {record.orderNumber}
      </Link>
    );
  }, []);

  const renderDeliveryWindowIdCell = React.useCallback(
    (record: TableRow) => {
      const deliveryWindowsData = allDeliveryWindows.filter(deliveryWindow =>
        record.deliveryWindowsIds.includes(deliveryWindow.deliveryWindow),
      );

      if (isEmpty(deliveryWindowsData)) {
        return null;
      }

      return <DeliveryWindowsColumn deliveryWindows={deliveryWindowsData} />;
    },
    [allDeliveryWindows],
  );

  const orderRowsData: TableRow[] = React.useMemo(
    () =>
      orders.map(order => ({
        deliveryWindowsIds: order.deliveryWindows,
        displayDate: order.orderDate,
        key: order.order,
        orderId: order.order,
        orderNumber: order.orderNumber,
        prepaymentAmount: getPrepaymentFromOrder(order)?.amount ?? '',
        status: order.status,
        total: order.totals.grandTotalWithoutTax,
        units: order.totals.itemsCount,
      })),
    [orders],
  );

  const ordersTotals = React.useMemo(() => {
    const emptyTotals = { prepaymentAmount: 0, total: 0, units: 0 };

    if (isEmpty(orders)) {
      return emptyTotals;
    }

    return (orders as Tuple<Order.Essentials | Order.Closed>).reduce(
      (acc, order) => ({
        prepaymentAmount: acc.prepaymentAmount + (getPrepaymentFromOrder(order)?.amountAsNumber ?? 0),
        total: acc.total + order.totals.grandTotalWithoutTaxAsNumber,
        units: acc.units + order.totals.itemsCount,
      }),
      emptyTotals,
    );
  }, [orders]);

  const hasPrepaidOrders = ordersTotals.prepaymentAmount > 0;
  const arePrepaymentsActive = !isEmpty(orders) && orders.some(order => isDefined(order.totals.prepaymentPriceAsNumber));

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('status', {
        cell: info => (
          <StatusCell status={info.getValue()} tooltip={<span className="body-4">{statusLabel[info.row.original.status]}</span>} />
        ),
        enableSorting: false,
        footer: t('totals:total_one'),
        header: t('common:status'),
        meta: {
          footerCellColspan: 4,
          noWrap: true,
          width: 50,
        },
      }),
      columnHelper.accessor('orderNumber', {
        cell: info => renderOrderNumberCell(info.row.original),
        header: t('orders:order_number', { orderNumber: '' }),
        sortingFn: 'alphanumeric',
      }),
      columnHelper.accessor('deliveryWindowsIds', {
        cell: info => renderDeliveryWindowIdCell(info.row.original),
        enableSorting: false,
        header: t('common:delivery_window_other'),
        meta: {
          isCollapsible: true,
          width: '100%',
        },
      }),
      columnHelper.accessor('displayDate', {
        cell: renderStandardCell,
        enableSorting: false,
        header: t('checkout:order_date'),
        meta: {
          minWidth: 150,
        },
      }),
      columnHelper.accessor('units', {
        cell: renderStandardCell,
        footer: () => ordersTotals.units,
        header: t('common:unit_other'),
        meta: {
          align: 'right',
        },
      }),
      columnHelper.accessor('total', {
        cell: renderStandardCell,
        footer: () => priceFormatter(ordersTotals.total),
        header: t('totals:total_no_tax'),
        meta: {
          align: 'right',
        },
        sortingFn: 'alphanumeric',
      }),
      ...(arePrepaymentsActive ?
        [
          columnHelper.accessor('prepaymentAmount', {
            cell: renderStandardCell,
            footer: () => priceFormatter(ordersTotals.prepaymentAmount),
            header: t('checkout:prepayment_split_by_order'),
            meta: {
              align: 'right',
            },
          }),
        ]
      : []),
    ],
    [
      t,
      arePrepaymentsActive,
      statusLabel,
      renderOrderNumberCell,
      renderDeliveryWindowIdCell,
      ordersTotals.units,
      ordersTotals.total,
      ordersTotals.prepaymentAmount,
      priceFormatter,
    ],
  );

  const table = useTable({
    columns,
    data: orderRowsData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="mx-auto w-full max-w-screen-xl px-4 pb-16 lg:px-8">
      <section className="mt-8">
        <h2 className="heading-2 sm:heading-1">{t('checkoutMessages:order_has_been_split.header')}</h2>
        <p className="mb-8 mt-4 body-2">
          <Trans t={t} i18nKey="checkoutMessages:order_has_been_split.details">
            Based on showroom settings the order has been <strong>split into separate orders</strong>.<br />
            You can view order summary by navigating to one of the orders from the table below.
          </Trans>
        </p>
        {hasPrepaidOrders && <Notification type="info" className="mb-8" message={t('checkout:prepayment_divided')} />}
        <Table
          table={table}
          getRowMarkerColor={row => getOrderStatusColor(row.original.status)}
          skeletonRows={2}
          noDataMessage={t('checkout:no_orders')}
          renderSkeletonCard={index => <CheckoutSplitCardSkeleton key={index} />}
          renderCard={row => <CheckoutSplitCard row={row} deliveryWindows={allDeliveryWindows} />}
        />
      </section>
    </div>
  );
};
